import { useState, useEffect, useContext } from 'react';
import { Application } from '../services/featureFlagService/interface';
import flagServiceSingleton from '../services/featureFlagService/featureFlagWebSocketSingleton';
import FeatureFlagService from '../services/featureFlagService/FeatureFlagService';
import { AuthContext } from '@src/utilities/authProvider';

/**
 * Custom React hook for fetching and managing a specific feature flag by its ID.
 * 
 * This hook performs an asynchronous request to retrieve feature flag information for a given application.
 * It uses a singleton service for tracking the feature flag and updates the component's state with the
 * feature flag's details, loading status, and any errors encountered during fetching.
 * 
 * @param {number} featureFlagId - The unique identifier for the feature flag to be retrieved.
 * @returns {Object} The hook returns an object containing the following properties:
 * - `featureFlag`: (Application | undefined) The detailed information about the application's feature flag, or undefined if not loaded.
 * - `isLoading`: (boolean) Indicates whether the feature flag information is currently being loaded.
 * - `error`: (string | null) Contains an error message if an error occurred during fetching, otherwise null.
 */
const useFeatureFlags = (featureFlagId: number): object => {
    // State to hold the feature flag details.
    const [featureFlag, setFeatureFlag] = useState<Application | undefined>(undefined);
    // State to indicate the loading status of the feature flag.
    const [isLoading, setIsLoading] = useState(true);
    // Access the authentication status from the AuthContext.
    const { isAuthenticated } = useContext(AuthContext);
    // State to hold any error messages encountered during the fetching process.
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Flag to keep track of component's mounted status to prevent state updates on unmounted components.
        let isMounted = true;
        // Initialize loading state and clear previous errors.
        setIsLoading(true);
        setError(null);

        const fetchFeatureFlag = async () => {
            try {
                // Asynchronously fetch the feature flag details using the singleton service.
                if (isMounted && isAuthenticated) { // Check if the component is still mounted before updating state.
                    const application = await flagServiceSingleton.trackFeatureFlag(featureFlagId);
                    console.log(application); // Logging the application details for debugging purposes.
                    // Update state with the fetched feature flag details.
                    setFeatureFlag(application);
                    // If the application and its feature flags are defined, update the FeatureFlagService.
                    if (application && application.featureFlags) {
                        FeatureFlagService.setFeatureFlagsForApplication(application.applicationId, application.featureFlags);
                        console.log(FeatureFlagService.getFeatureFlagsForApplication(featureFlagId)); // Logging for debugging.
                    }
                    // Mark the loading process as finished.
                    setIsLoading(false);
                }
            } catch (error) {
                if (isMounted) { // Ensure the component is still mounted before setting error state.
                    console.error("Failed to load feature flag:", error); // Log the error for debugging.
                    // Update state with error message and mark loading as finished.
                    setError("Failed to load feature flag");
                    setIsLoading(false);
                }
            }
        };

        fetchFeatureFlag(); // Execute the fetch operation.

        return () => {
            isMounted = false; // Set isMounted to false when the component unmounts to prevent state updates.
        };
    }, [featureFlagId, isAuthenticated]); // Rerun the effect if the featureFlagId changes.

    // Return the feature flag details, loading status, and any errors encountered.
    return { featureFlag, isLoading, error };
}

export default useFeatureFlags;
